import { Routes } from '@angular/router';
import { canActivateAdmin, canActivateAuth } from './core/auth/auth.guard';

export const appRoutes: Routes = [
	// Default landing — admins arrive on /updates
	{ path: '', redirectTo: '/updates', pathMatch: 'full' },

	// Updates feature — Plan 4 extracted UpdatesPageComponent from the legacy
	// TableComponent god-component; /updates/:id reuses the same page until
	// Plan 8 promotes it to a drawer-aware route.
	{
		path: 'updates',
		canActivate: [canActivateAuth],
		loadComponent: () =>
			import('./features/updates/updates-page/updates-page.component').then(
				(m) => m.UpdatesPageComponent,
			),
	},
	{
		path: 'updates/:id',
		canActivate: [canActivateAuth],
		loadComponent: () =>
			import('./features/updates/updates-page/updates-page.component').then(
				(m) => m.UpdatesPageComponent,
			),
	},

	// Licenses feature
	{
		path: 'licenses',
		canActivate: [canActivateAuth],
		loadComponent: () =>
			import('./features/licenses/licenses-page/licenses-page.component').then(
				(m) => m.LicensesPageComponent,
			),
	},

	// License groups feature (Odoo 5133 / Phase 13)
	{
		path: 'licensegroups',
		canActivate: [canActivateAuth],
		loadComponent: () =>
			import('./features/license-groups/license-groups-page.component').then(
				(m) => m.LicenseGroupsPageComponent,
			),
	},

	// EMS licence directory (Odoo 5133) — société / gammiste / features per key,
	// synced from Sentinel EMS. Standalone directory (keys are hashed, no shared
	// number to join onto the Licenses list on dev).
	{
		path: 'license-directory',
		// odoo-5133 — reserve aux administrateurs, comme le serveur. L'entree de barre
		// laterale etait deja cachee aux comptes clients, mais la route restait ouverte a
		// qui tapait l'adresse : l'annuaire EMS est global, il nomme la societe et le
		// gammiste de tous les clients a la fois.
		canActivate: [canActivateAdmin],
		loadComponent: () =>
			import('./features/license-directory/license-directory-page.component').then(
				(m) => m.LicenseDirectoryPageComponent,
			),
	},

	// Per-poste telemetry (odoo-8118) — admin-only. Lists one machine per row
	// with the Cover / cover-systray versions and installed providers/files,
	// fed by the launcher's existing POST /api/update. Same guard set as the
	// other admin routes (Packages / License directory).
	{
		path: 'telemetry',
		// odoo-8118 — the poste telemetry is the tenant's own machines; the API is
		// tenant-scoped (a non-admin sees only their tenant, an admin bypasses and
		// sees all), so this is authenticated-only like updates/licences/groups
		// rather than admin-only. Lets the client IT admin (e.g. Le Vérandier) see
		// their own postes without granting them a cross-tenant admin role.
		canActivate: [canActivateAuth],
		loadComponent: () =>
			import('./features/telemetry/telemetry-page.component').then(
				(m) => m.TelemetryPageComponent,
			),
	},

	// Packages feature — authentifiee, comme les mises a jour et les licences.
	//
	// Elle etait reservee aux administrateurs par heritage : a l'epoque, un paquet
	// naissait tout seul a chaque creation de mise a jour et personne d'autre n'avait
	// a en fabriquer. Depuis que le paquet se cree explicitement, fermer cette page a
	// un compte client revient a lui interdire de deposer quoi que ce soit — constate
	// par JP Maron le 2026-08-18 sur le compte de Sebastien Royer, qui n'avait meme
	// pas l'onglet.
	//
	// L'API etait deja prete : la liste est filtree sur le tenant du jeton (chacun ne
	// voit que ses paquets) et la creation estampille ce meme tenant, sans que le
	// client puisse le choisir. Seule la suppression reste refusee a un non-admin, et
	// l'action est masquee pour lui plutot que de mener a un 403.
	{
		path: 'packages',
		canActivate: [canActivateAuth],
		loadComponent: () =>
			import('./features/packages/packages-page/packages-page.component').then(
				(m) => m.PackagesPageComponent,
			),
	},

	// Aide (odoo-5133) — le guide d'utilisation et les notes de version, servis depuis les
	// fichiers Markdown du depot. Authentifiee comme le reste : la documentation decrit un
	// outil interne, elle n'a pas a etre publique, et une page d'aide derriere le meme
	// portail evite d'avoir a expliquer ou la trouver.
	{
		path: 'aide',
		canActivate: [canActivateAuth],
		loadComponent: () => import('./features/help/help-page.component').then((m) => m.HelpPageComponent),
	},

	// Fallback pages
	{
		path: 'forbidden',
		loadComponent: () => import('./features/forbidden/forbidden.component').then(m => m.ForbiddenComponent),
	},
	{
		path: '404',
		loadComponent: () => import('./features/not-found/not-found.component').then(m => m.NotFoundComponent),
	},

	// Catch-all: any unknown URL routes to /404 (preserves SPA bookmark links — Pitfall 11 mitigation §1)
	{ path: '**', redirectTo: '/404' },
];
